import { TemplateSelectorModal } from '@salecycle/experimental/Modal/TemplateSelectorModal';
import { T } from '@salecycle/experimental/Typography/Typography';
import { Button, Icon, ImageArea, LinkButton } from '@salecycle/ui-component-library';
import { useFeatureFlags } from 'core/app/AppContext';
import { OffsiteCampaignType } from 'models/offsite/Campaign/OffsiteCampaign';
import { Template, TemplateType } from 'models/template/Template';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useNavigateToEmailTemplates } from 'sections/bulk-retargeting-campaigns/CampaignEditor/hooks/useNavigateToEmailTemplates';
import { useSetTemplate } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/actions/useSetTemplate';
import { TargetAudienceType } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/CampaignEditorState';
import { useCampaignEditorContext } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/hooks/useCampaignEditorContext';
import { useGetTemplates } from 'server/templates/queries/getTemplates/useGetTemplates';
import { useTheme } from 'styled-components';

export const SelectedContent = ({
  targetAudience,
  toggleModal,
}: {
  toggleModal: () => void;
  targetAudience: TargetAudienceType;
}) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const { siteId } = useParams<{ siteId: string }>();
  const { state } = useCampaignEditorContext();

  const template = state.campaign.template[targetAudience];

  const src = `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${siteId}/${template?.id}/${template
    ?.supportedLanguages?.[0]?.language ?? ''}.template.jpg`;

  const navigateToEmailTemplates = useNavigateToEmailTemplates();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing5 }}>
      <div style={{ display: 'flex', gap: theme.designToken.spacing5 }}>
        <ImageArea
          src={src}
          width={100}
          height={100}
          fallback="/images/designs/design_gallery/default_template_placeholder_small.png"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing2 }}>
          <T>{template?.templateName}</T>
          <LinkButton ariaLabel="" onClick={navigateToEmailTemplates(false)} disabled={state?.readOnly}>
            {t('bulkRetargetingCampaigns.editor.editTemplate')}
            <div style={{ marginLeft: theme.designToken.spacing1 }} />
            <div style={{ position: 'relative', margin: 'auto 0', top: '0', bottom: '0' }}>
              <Icon icon="external-link-square" />
            </div>
          </LinkButton>
        </div>
      </div>
      <div>
        <Button
          aria-label={t('bulkRetargetingCampaigns.editor.changeSelection')}
          onClick={toggleModal}
          variation="secondary"
          disabled={state?.readOnly}
        >
          {t('bulkRetargetingCampaigns.editor.changeSelection')}
        </Button>
      </div>
    </div>
  );
};

export const UnSelectedContent = ({ toggleModal }: { toggleModal: () => void }) => {
  const { t } = useTranslation('campaigns');
  const { state } = useCampaignEditorContext();

  return (
    <div>
      <Button
        aria-label={t('bulkRetargetingCampaigns.editor.selectATemplate')}
        onClick={toggleModal}
        disabled={state?.readOnly}
      >
        {t('bulkRetargetingCampaigns.editor.selectATemplate')}
      </Button>
    </div>
  );
};

export interface EmailTemplateProps {
  targetAudience: TargetAudienceType;
}

export const EmailTemplate = ({ targetAudience }: EmailTemplateProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['templateSelectModal', 'campaigns']);
  const { siteId } = useParams<{ siteId: string }>();
  const { flagEnabled } = useFeatureFlags();
  const { state } = useCampaignEditorContext();

  const [open, setOpen] = useState(false);
  const toggleModal = useCallback(() => setOpen(!open), [open]);

  const getThumbnailUrl = useCallback(
    (template: Template) =>
      `${import.meta.env.VITE_TEMPLATE_THUMBNAIL_URL}/sites/${siteId}/${template[targetAudience]?.id}/${template[
        targetAudience
      ]?.supportedLanguages?.[0]?.language ?? ''}.template.jpg`,
    [targetAudience, siteId],
  );

  const query = useGetTemplates(siteId, {
    isDisplayOnlyEnabled: flagEnabled('ld-onsite-display-only'),
    filterBy: [TemplateType.BulkRetargeting],
  });

  const setTemplate = useSetTemplate();
  const setTemplateCallback = (template: Template) => setTemplate(targetAudience, template);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing4 }}>
        <T variant="headingH3">{t('campaigns:bulkRetargetingCampaigns.editor.emailTemplate')}</T>
        {state.campaign.template[targetAudience] ? (
          <SelectedContent toggleModal={toggleModal} targetAudience={targetAudience} />
        ) : (
          <UnSelectedContent toggleModal={toggleModal} />
        )}
      </div>
      <TemplateSelectorModal
        open={open}
        onClose={toggleModal}
        t={t}
        templatesQuery={query}
        templateTypes={[OffsiteCampaignType.BulkRetargting]}
        onConfirm={setTemplateCallback}
        templateFallbackThumbnailUrl="/images/designs/design_gallery/default_template_placeholder_small.png"
        getTemplateThumbnailUrl={getThumbnailUrl}
        channel="email"
      />
    </>
  );
};
